import { Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import DashboardPage from "./pages/DashboardPage";
import LeadsPage from "./pages/LeadsPage";
import ProjectsPage from "./pages/ProjectsPage";
import VariantsPage from "./pages/VariantsPage";
import SettingsPage from "./pages/SettingsPage";
import ProjectAnalyticsPage from "./pages/ProjectAnalyticsPage";
import KanbanPage from "./pages/KanbanPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import BrandingPage from "./pages/BrandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import CrmProjectDetailPage from "./pages/CrmProjectDetailPage";
import CrmProjectProgramsPage from "./pages/CrmProjectProgramsPage";
import CrmProjectModulesPage from "./pages/CrmProjectModulesPage";
import CrmModuleEditorPage from "./pages/CrmModuleEditorPage";
import VerticalSeedsPage from "./pages/VerticalSeedsPage";
import DevDiagnosticsPage from "./pages/DevDiagnosticsPage";
import ProjectIntelligencePage from "./pages/ProjectIntelligencePage";
import ProjectBrandingPage from "./pages/ProjectBrandingPage";
import AccountAuditPage from "./pages/AccountAuditPage";
import AccountsPage from "./pages/AccountsPage";
import AdminJobsPage from "./pages/AdminJobsPage";

import AdminPlatformSettingsPage from "./pages/settings/AdminPlatformSettingsPage";
import AdminGenerationSettingsPage from "./pages/settings/AdminGenerationSettingsPage";
import AdminAiSettingsPage from "./pages/settings/AdminAiSettingsPage";
import ClientUsageSettingsPage from "./pages/settings/ClientUsageSettingsPage";
import ClientCrmSettingsPage from "./pages/settings/ClientCrmSettingsPage";
import ClientBusinessSettingsPage from "./pages/settings/ClientBusinessSettingsPage";
import AdminAiProviderRoutesPage from "./pages/AdminAiProviderRoutesPage";
import AdminMonitoringPage from "./pages/admin/AdminMonitoringPage";

import {
    getSession,
    isAuthenticated,
    isSuperAdminUser,
    refreshSessionFromServer
} from "./services/authService";

import "./styles/theme.css";
import "./styles/layout.css";
import SystemLogsPage from "./pages/SystemLogsPage";
import { applyThemeMode, getStoredThemeMode } from "./utils/themeMode";
import "./styles/adminLightMode.css";
import SupportTicketsPage from "./pages/SupportTicketsPage";
import SupportDashboardPage from "./pages/SupportDashboardPage";
import SupportTicketDetailPage from "./pages/SupportTicketDetailPage";
import KnowledgeBaseAdminPage from "./pages/KnowledgeBaseAdminPage";
import TeamManagementPage from "./pages/TeamManagementPage";
import DemoShowcasePage from "./pages/DemoShowcasePage";

function PrivateRoute({ children }) {
    return isAuthenticated() ? children : <Navigate to="/login" replace />;
}

function SuperAdminRoute({ children }) {
    if (!isAuthenticated()) {
        return <Navigate to="/login" replace />;
    }

    return isSuperAdminUser() ? children : <Navigate to="/" replace />;
}

export default function App() {
    const location = useLocation(); // ⚠️ move BrowserRouter up if needed
    const session = getSession();

    useEffect(() => {

        const map = {
            "/": "Dashboard",
            "/projects": "Projects",
            "/leads": "Leads",
            "/variants": "Variants",
            "/settings": "Settings"
        };

        const base = "Shriansh Nexus";
        const page = map[location.pathname] || "Admin Portal";

        document.title = `${page} | ${base}`;

        let mounted = true;

        const bootstrap = async () => {
            try {
                await refreshSessionFromServer();
            } catch {
                // no active cookie session or expired session
            } finally {
                if (mounted) {
                }
            }
        };

        bootstrap();

        applyThemeMode(getStoredThemeMode());

        return () => {
            mounted = false;
        };
    }, [location.pathname]);

    useEffect(() => {
        if (session?.primaryColor) {
            document.documentElement.style.setProperty(
                "--primary",
                session.primaryColor
            );
        }
    }, [session?.primaryColor]);

    return (
        <>
            <Routes>

                <Route
                    path="/accounts"
                    element={
                        <PrivateRoute>
                            <AccountsPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/accounts/:accountId/audit"
                    element={
                        <SuperAdminRoute>
                            <AccountAuditPage />
                        </SuperAdminRoute>
                    }
                />

                <Route
                    path="/crm/projects/:projectId/branding"
                    element={<ProjectBrandingPage />}
                />

                <Route path="/admin/monitoring"

                    element={
                        <PrivateRoute>
                            <AdminMonitoringPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/support"
                    element={
                        <PrivateRoute>
                            <SupportTicketsPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/support/tickets/:ticketId"
                    element={
                        <PrivateRoute>
                            <SupportTicketDetailPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/support/dashboard"
                    element={
                        <PrivateRoute>
                            <SupportDashboardPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/knowledge-base"
                    element={
                        <SuperAdminRoute>
                            <KnowledgeBaseAdminPage />
                        </SuperAdminRoute>
                    }
                />

                <Route path="/demo-showcase" element={<DemoShowcasePage />} />

                <Route path="/crm/projects/:projectId/intelligence" element={<ProjectIntelligencePage />} />

                <Route path="/dev/diagnostics" element={<DevDiagnosticsPage />} />
                <Route
                    path="/crm/projects/:projectId/modules/:moduleKey"
                    element={<CrmModuleEditorPage />}
                />
                <Route
                    path="/crm/projects/:projectId"
                    element={
                        <PrivateRoute>
                            <CrmProjectDetailPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/crm/projects/:projectId/programs"
                    element={
                        <PrivateRoute>
                            <CrmProjectProgramsPage />
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/vertical-seeds"
                    element={
                        <SuperAdminRoute>
                            <VerticalSeedsPage />
                        </SuperAdminRoute>
                    }
                />
                <Route
                    path="/ai-providers"
                    element={
                        <SuperAdminRoute>
                            <AdminAiProviderRoutesPage />
                        </SuperAdminRoute>
                    }
                />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                <Route path="/crm/projects/:projectId/modules" element={<CrmProjectModulesPage />} />

                <Route
                    path="/branding"
                    element={
                        <PrivateRoute>
                            <BrandingPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/"
                    element={
                        <PrivateRoute>
                            <DashboardPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/system-jobs"
                    element={
                        <SuperAdminRoute>
                            <AdminJobsPage />
                        </SuperAdminRoute>
                    }
                />

                <Route
                    path="/leads"
                    element={
                        <PrivateRoute>
                            <LeadsPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/projects"
                    element={
                        <PrivateRoute>
                            <ProjectsPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/projects/:projectId"
                    element={
                        <PrivateRoute>
                            <ProjectAnalyticsPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/kanban"
                    element={
                        <SuperAdminRoute>
                            <KanbanPage />
                        </SuperAdminRoute>
                    }
                />

                <Route
                    path="/variants"
                    element={
                        <SuperAdminRoute>
                            <VariantsPage />
                        </SuperAdminRoute>
                    }
                />

                <Route
                    path="/settings"
                    element={
                        <PrivateRoute>
                            <SettingsPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/settings/client"
                    element={
                        <PrivateRoute>
                            <ClientBusinessSettingsPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/settings/crm"
                    element={
                        <PrivateRoute>
                            <ClientCrmSettingsPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/settings/usage"
                    element={
                        <PrivateRoute>
                            <ClientUsageSettingsPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/admin/settings/ai"
                    element={
                        <SuperAdminRoute>
                            <AdminAiSettingsPage />
                        </SuperAdminRoute>
                    }
                />

                <Route
                    path="/admin/settings/generation"
                    element={
                        <SuperAdminRoute>
                            <AdminGenerationSettingsPage />
                        </SuperAdminRoute>
                    }
                />

                <Route
                    path="/admin/settings/platform"
                    element={
                        <SuperAdminRoute>
                            <AdminPlatformSettingsPage />
                        </SuperAdminRoute>
                    }
                />

                <Route
                    path="/system-logs"
                    element={
                        <SuperAdminRoute>
                            <SystemLogsPage />
                        </SuperAdminRoute>
                    }
                />

                <Route
                    path="/team"
                    element={
                        <PrivateRoute>
                            <TeamManagementPage />
                        </PrivateRoute>
                    }
                />

            </Routes>

            <Toaster
                position="top-right"
                toastOptions={{
                    style: {
                        background: "#111827",
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.08)"
                    }
                }}
            />
        </>
    );
}